import axios from 'axios';
import secureStorage from './secureStorage';
import { installPageCache } from './pageCache';
import { getConstraintContext } from './constraintContext';
import { readActiveProvenance } from './solutionFinderProvenance';

const BAKED_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve the API base URL defensively.
 *
 * Normally we just use the build-time `REACT_APP_BACKEND_URL`. But when the
 * app is deployed behind a custom domain via DNS CNAME (e.g. GoDaddy →
 * Emergent), the baked value can still point at the platform-generated
 * preview/internal hostname — making every `/api/*` call cross-origin to a
 * domain that (a) isn't on the backend's CORS allow-list, and (b) ends up
 * setting auth cookies on the wrong domain. The OAuth redirect-loop bug in
 * production stems from exactly this mismatch.
 *
 * Fix: when we can safely detect a mismatch, prefer same-origin. Same-origin
 * is always reachable because Emergent's Kubernetes ingress routes `/api/*`
 * to the backend on every domain we serve from.
 *
 * Rules — kept surgical so preview + local dev behavior is unchanged:
 *   • No window (SSR / worker) → use baked value.
 *   • Baked value is empty → use current origin.
 *   • Baked value matches origin (or is a subpath of it) → use baked.
 *   • Baked value is a preview URL AND current origin is NOT a preview URL
 *     → treat as deployment drift, use current origin.
 *   • Anything else → trust the baked value (rare edge case; keep it simple).
 */
function resolveBackendUrl() {
  if (typeof window === 'undefined') return BAKED_BACKEND_URL;
  const origin = window.location.origin;
  if (!BAKED_BACKEND_URL) return origin;
  try {
    const baked = new URL(BAKED_BACKEND_URL);
    if (baked.origin === origin) return BAKED_BACKEND_URL;
    const bakedIsPreview = baked.hostname.endsWith('.preview.emergentagent.com');
    const originIsPreview = origin.includes('.preview.emergentagent.com');
    const originIsLocalhost = origin.startsWith('http://localhost');
    if (bakedIsPreview && !originIsPreview && !originIsLocalhost) {
      // Deployment drift detected — front-end is live on a custom domain
      // but the build still carries the preview URL. Route to same-origin.
      return origin;
    }
    return BAKED_BACKEND_URL;
  } catch {
    return BAKED_BACKEND_URL;
  }
}

const BACKEND_URL = resolveBackendUrl();
const API = `${BACKEND_URL}/api`;

// Boot diagnostic — logged once at module load so anyone reproducing an
// auth/CORS bug on a deployed build can read the resolved API base out of
// the browser console without needing special access. Intentionally cheap
// (single log line) and informational only.
if (typeof window !== 'undefined') {
  try {
    const resolved = BACKEND_URL;
    const baked = BAKED_BACKEND_URL || '(unset)';
    const drifted = resolved !== baked;
    // eslint-disable-next-line no-console
    console.info(
      `%c[LEI] API base: ${resolved}${drifted ? ` (baked: ${baked})` : ''} · origin: ${window.location.origin}`,
      'color:#0ea5e9;font-weight:600',
    );
    if (drifted) {
      // eslint-disable-next-line no-console
      console.warn(
        `[LEI] API base auto-corrected to same-origin. The build-time ` +
        `REACT_APP_BACKEND_URL (${baked}) didn't match the current origin ` +
        `(${window.location.origin}) — this app was deployed to a custom ` +
        `domain without the env var being updated at build time. The ` +
        `runtime fallback keeps auth/CORS working, but for the cleanest ` +
        `setup, update REACT_APP_BACKEND_URL on the Emergent deployment.`
      );
    }
  } catch { /* ignore logging failures */ }
}

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add request interceptor to include auth token and View-As-Org header
api.interceptors.request.use(
  (config) => {
    const token = secureStorage.getItem('session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach View-As-Org header for Super Admin org preview
    try {
      const viewAsOrg = sessionStorage.getItem('viewAsOrg');
      if (viewAsOrg) {
        const parsed = JSON.parse(viewAsOrg);
        if (parsed?.id) {
          config.headers['X-View-As-Org'] = parsed.id;
        }
      }
    } catch { /* ignore parse errors */ }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const currentPath = window.location.pathname;
      const currentHash = window.location.hash || '';
      
      // NEVER interfere during OAuth callback flow or auth-related endpoints.
      // In particular, `/auth/me` is allowed to 401 without hard-reloading
      // the page — during post-login RBAC enrichment, a transient 401 must
      // not wipe the fresh session and redirect to /login.
      const isOAuthCallback = currentHash.includes('session_id=');
      const isAuthEndpoint = url.includes('/auth/google/session') || 
                             url.includes('/auth/clear-session') ||
                             url.includes('/auth/login') ||
                             url.includes('/auth/register') ||
                             url.includes('/auth/me') ||
                             // Background UI-copy fetch — fires on public
                             // share pages too; a 401 here must never wipe
                             // the session or hard-redirect to /login.
                             url.includes('/mc/messaging/resolved');
      const isOnLoginPage = currentPath === '/login' || currentPath === '/register';
      // Public, token-signed viewer routes — never bounce an anonymous
      // CFO/board reader to the login screen.
      const isPublicSharePage = currentPath.startsWith('/share/');
      // Super-admin "View as org" preview: a 401/permission blip on an
      // org-scoped data request (carrying X-View-As-Org) must NOT wipe the
      // real session and force a re-login. This was the production symptom
      // where switching the previewed org blanked the page and kicked the
      // super admin back to /login. Let the component render empty instead.
      const isOrgPreviewRequest = !!(error.config?.headers &&
        (error.config.headers['X-View-As-Org'] || error.config.headers['x-view-as-org']));

      if (!isOAuthCallback && !isAuthEndpoint && !isPublicSharePage && !isOrgPreviewRequest) {
        secureStorage.removeItem('session_token');
        // Also drop any stale httpOnly cookie server-side before bouncing.
        try { api.post('/auth/clear-session').catch(() => {}); } catch { /* noop */ }
        // Redirect to login if not already there
        if (!isOnLoginPage) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

installPageCache(api);

// LEI Measures and KPIs
export const getLEIMeasures = () => api.get('/lei-measures');
export const getKPIs = () => api.get('/kpis');
export const getLEIKPIWeights = () => api.get('/lei-kpi-weights');

// Calculations
export const calculateImpact = (data) => api.post('/calculate', data);
export const calculatePortfolio = (data) => api.post('/calculate-portfolio', data);
export const calculateFullMatrix = (data) => api.post('/calculate-full-matrix', data);
export const generateNarrative = (data) => api.post('/generate-narrative', data);

// Scenarios
// Scenario saves are auto-tagged with the session's Mission Control
// constraint context (if any) so Compare Scenarios can rank runs by
// expected value recovery. Explicit data wins over the session tag.
export const saveScenario = (data) => {
  // Auto-inject the narrative-bridge provenance + the legacy
  // constraint_context (kept for back-compat with Compare Scenarios)
  // so every saved scenario carries "why it was modelled" metadata.
  return api.post('/scenarios', {
    constraint_context: getConstraintContext(),
    provenance: readActiveProvenance({ kpis: data?._kpis_for_provenance }),
    ...data,
  });
};
export const getScenarios = (orgId) => api.get(orgId ? `/scenarios?organization_id=${orgId}` : '/scenarios');
export const getScenario = (id) => api.get(`/scenarios/${id}`);
export const deleteScenario = (id) => api.delete(`/scenarios/${id}`);

// Export/Import
export const exportToExcel = async (data) => {
  const response = await api.post('/export/excel', data, {
    responseType: 'blob',
  });
  return response;
};

export const exportToPDF = async (data) => {
  const response = await api.post('/export-pdf', data, {
    responseType: 'blob',
  });
  return response;
};

export const getExcelTemplate = async () => {
  const response = await api.get('/export/template', {
    responseType: 'blob',
  });
  return response;
};

export const getAdvancedTemplate = async () => {
  const response = await api.get('/export/advanced-template', {
    responseType: 'blob',
  });
  return response;
};

export const importFromExcel = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/import/excel', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

export const importAdvancedData = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/import/advanced', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

// Import Templates
export const getImportTemplates = async () => {
  const response = await api.get('/import/templates');
  return response;
};

export const createImportTemplate = async (data) => {
  const response = await api.post('/import/templates', data);
  return response;
};

export const getImportTemplate = async (templateId) => {
  const response = await api.get(`/import/templates/${templateId}`);
  return response;
};

export const deleteImportTemplate = async (templateId) => {
  const response = await api.delete(`/import/templates/${templateId}`);
  return response;
};

export const downloadTemplateExcel = async (templateId) => {
  const response = await api.get(`/import/templates/${templateId}/download`, {
    responseType: 'blob',
  });
  return response;
};

// Import History
export const getImportHistory = async () => {
  const response = await api.get('/import/history');
  return response;
};

export const rerunImport = async (historyId) => {
  const response = await api.post(`/import/history/${historyId}/rerun`);
  return response;
};

export const deleteImportHistory = async (historyId) => {
  const response = await api.delete(`/import/history/${historyId}`);
  return response;
};

// Roadmap Export
export const exportRoadmapPDF = async (data) => {
  const response = await api.post('/export/roadmap/pdf', data, {
    responseType: 'blob',
  });
  return response;
};

export const exportRoadmapExcel = async (data) => {
  const response = await api.post('/export/roadmap/excel', data, {
    responseType: 'blob',
  });
  return response;
};

// Email Sharing
export const shareScenarioEmail = async (data) => {
  const response = await api.post('/share-scenario-email', data);
  return response;
};

// Scheduled Reports
export const getScheduledReports = async () => {
  const response = await api.get('/scheduled-reports');
  return response;
};

export const createScheduledReport = async (data) => {
  const response = await api.post('/scheduled-reports', data);
  return response;
};

export const updateScheduledReport = async (reportId, data) => {
  const response = await api.put(`/scheduled-reports/${reportId}`, data);
  return response;
};

export const deleteScheduledReport = async (reportId) => {
  const response = await api.delete(`/scheduled-reports/${reportId}`);
  return response;
};

export const toggleScheduledReport = async (reportId) => {
  const response = await api.post(`/scheduled-reports/${reportId}/toggle`);
  return response;
};

export const sendScheduledReportNow = async (reportId) => {
  const response = await api.post(`/scheduled-reports/${reportId}/send-now`);
  return response;
};

// Accuracy Leaderboard
export const getAccuracyLeaderboard = async () => {
  const response = await api.get('/accuracy/leaderboard');
  return response;
};

export const getMyAccuracyStats = async () => {
  const response = await api.get('/accuracy/my-stats');
  return response;
};

export const recordProjectionAccuracy = async (projectedId, actualId) => {
  const response = await api.post(`/accuracy/record?projected_scenario_id=${projectedId}&actual_scenario_id=${actualId}`);
  return response;
};

export const getSchedulerStatus = async () => {
  const response = await api.get('/scheduler-status');
  return response;
};

// Custom Presets
export const getCustomPresets = async () => {
  const response = await api.get('/presets');
  return response;
};

export const createCustomPreset = async (data) => {
  const response = await api.post('/presets', data);
  return response;
};

export const updateCustomPreset = async (presetId, data) => {
  const response = await api.put(`/presets/${presetId}`, data);
  return response;
};

export const deleteCustomPreset = async (presetId) => {
  const response = await api.delete(`/presets/${presetId}`);
  return response;
};

// Logo Management
export const uploadLogo = async (logoBase64) => {
  const response = await api.post('/auth/logo', { logo: logoBase64 });
  return response;
};

export const getLogo = async () => {
  const response = await api.get('/auth/logo');
  return response;
};

export const deleteLogo = async () => {
  const response = await api.delete('/auth/logo');
  return response;
};

export default api;
